import { useEffect, useState } from 'react';
import { openingHoursSpecification } from '../data/business';

/**
 * Live open/closed status, computed in Seattle time from the same
 * openingHoursSpecification the structured data uses, so the visible status
 * can never disagree with the hours Google reads.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** Service windows per weekday (0 = Sunday), as [open, close] minutes. */
export const serviceWindows: [number, number][][] = DAY_NAMES.map((day) =>
  openingHoursSpecification
    .filter((spec) => (Array.isArray(spec.dayOfWeek) ? spec.dayOfWeek : [spec.dayOfWeek]).includes(day))
    .map((spec): [number, number] => [toMinutes(spec.opens), toMinutes(spec.closes)])
    .sort((a, b) => a[0] - b[0]),
);

export const formatClock = (minutes: number) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${suffix}`;
};

export type OpenState = 'open' | 'closing-soon' | 'closed';

export interface OpenStatus {
  state: OpenState;
  /** One line, e.g. "Open now · until 9 PM". */
  headline: string;
  /** Supporting line, e.g. "Kitchen breaks at 3 PM, back at 4 PM." */
  detail: string;
  /** Today's weekday in Seattle, 0 = Sunday. */
  day: number;
}

const seattleNow = (now: Date) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value]),
  );
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  return { day, minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute) };
};

export const getOpenStatus = (now = new Date()): OpenStatus => {
  const { day, minutes } = seattleNow(now);
  const today = serviceWindows[day];

  for (const [open, close] of today) {
    if (minutes >= open && minutes < close) {
      const left = close - minutes;
      const next = today.find(([o]) => o > minutes);
      return {
        state: left <= 30 ? 'closing-soon' : 'open',
        headline: `${left <= 30 ? 'Closing soon' : 'Open now'} · until ${formatClock(close)}`,
        detail: next
          ? `Kitchen breaks at ${formatClock(close)}, back at ${formatClock(next[0])}.`
          : `Kitchen closes at ${formatClock(close)}, Seattle time.`,
        day,
      };
    }
  }

  const laterToday = today.find(([o]) => o > minutes);
  if (laterToday) {
    const between = today.some(([, c]) => c <= minutes);
    return {
      state: 'closed',
      headline: between
        ? `Between services · back at ${formatClock(laterToday[0])}`
        : `Closed now · opens at ${formatClock(laterToday[0])}`,
      detail: 'Hours are Seattle time.',
      day,
    };
  }

  for (let k = 1; k <= 7; k += 1) {
    const nextDay = (day + k) % 7;
    const first = serviceWindows[nextDay][0];
    if (first) {
      return {
        state: 'closed',
        headline: `Closed now · opens ${k === 1 ? 'tomorrow' : DAY_NAMES[nextDay]} at ${formatClock(first[0])}`,
        detail: 'Hours are Seattle time.',
        day,
      };
    }
  }

  return { state: 'closed', headline: 'Closed now', detail: '', day };
};

/** Re-evaluates every 30 seconds so a page left open stays truthful. */
export const useOpenStatus = () => {
  const [status, setStatus] = useState(() => getOpenStatus());
  useEffect(() => {
    const id = window.setInterval(() => setStatus(getOpenStatus()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return status;
};
